import React from "react";

import { TLBaseShape, RecordProps, T, HTMLContainer, BaseBoxShapeUtil, TLResizeInfo } from "@tldraw/tldraw";
import { getDashArray } from "../new-geo-shape-helpers";
import { HOVER_EFFECT } from "./constants";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { defaultScale } from "../constants";
import { getTriangleCornerRadii, roundedTrianglePath } from "./utils/cornerRadii";

type CustomTriangleUtilType = TLBaseShape<
  "customTriangle",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    rounded: number;
    // Optional per-vertex overrides for `rounded`. A triangle has 3
    // corners (top apex / bottom-right / bottom-left); each is read
    // independently and falls back to `rounded` when `undefined`. The
    // CornerRadiusPopover renders a triangle-variant UI when the
    // selected shape is a customTriangle. See `utils/cornerRadii.ts`.
    roundedTop?: number;
    roundedBR?: number;
    roundedBL?: number;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    filterBlur?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
    [key: string]: any;
  }
>;

export class CustomTriangleUtil extends BaseBoxShapeUtil<CustomTriangleUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: any) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }
  static override type = "customTriangle" as const;
  static override props: any;

  getDefaultProps(): CustomTriangleUtilType["props"] {
    return {
      w: 100,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      rounded: 0,
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      filterBlur: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getGeometry(shape: CustomTriangleUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }
  hideSelectionBoundsFg(_shape: CustomTriangleUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomTriangleUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomTriangleUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: CustomTriangleUtilType): boolean {
    const props = this.shapeUtilHelper.getSafeProps(shape);
    return !!props.isScale;
  }
  onResize(shape: CustomTriangleUtilType, info: TLResizeInfo<CustomTriangleUtilType>) {
    const props = this.shapeUtilHelper.getSafeProps(shape);
    if (props.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  component(shape: CustomTriangleUtilType) {
    const props = this.shapeUtilHelper.getSafeProps(shape);
    const { w, h, outlineColor, dash, strokeDasharray, outShadowArray, inShadowArray } = props;

    // Per-vertex radii (top apex / bottom-right / bottom-left) with
    // fallback to the uniform `rounded` value. `roundedTrianglePath`
    // handles clamping so a per-corner value can't exceed half of its
    // adjacent edge.
    const triRadii = getTriangleCornerRadii(shape.props);
    const pathData = roundedTrianglePath(w, h, triRadii);

    const shadowFilterId = `shadow-${shape.id}`; // Unique ID for the shadow filter
    const innerShadowFilterId = `inner-shadow-${shape.id}`; // Unique ID for the inner shadow filter
    const dashArray = strokeDasharray || getDashArray(dash);

    return this.shapeUtilHelper.useRenderedShape(
      shape,
      pathData,
      outlineColor,
      dashArray,
      shadowFilterId,
      outShadowArray,
      inShadowArray,
      this.indicator.bind(this)
    );
  }

  addDefaultColorMix(shape: CustomTriangleUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomTriangleUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomTriangleUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  // Indicator function to display shape in editor
  indicator(shape: CustomTriangleUtilType) {
    const props = this.shapeUtilHelper.getSafeProps(shape);
    const {
      h,
      w,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
      flipX = false,
      flipY = false,
    } = props;
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    // Per-vertex radii so the selection outline matches the actual
    // path. Shared helper handles uniform fallback + clamping.
    const triRadii = getTriangleCornerRadii(shape.props);
    const pathData = roundedTrianglePath(w, h, triRadii);

    // Calculate flip transform for SVG
    const getFlipTransform = () => {
      const borderTranslateX = isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0;
      const borderTranslateY = isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0;

      if (!flipX && !flipY) {
        return `translate(${borderTranslateX}, ${borderTranslateY})`;
      }

      const flipTranslateX = flipX ? adjustedWidth : 0;
      const flipTranslateY = flipY ? adjustedHeight : 0;
      const scaleX = flipX ? -1 : 1;
      const scaleY = flipY ? -1 : 1;

      // Combine border translate with flip transform
      return `translate(${borderTranslateX + flipTranslateX}, ${
        borderTranslateY + flipTranslateY
      }) scale(${scaleX}, ${scaleY})`;
    };

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <path
        d={pathData}
        transform={getFlipTransform()}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}
